import { useCallback, useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  Pressable,
  ActivityIndicator,
  TextInput,
} from "react-native";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../../onboarding/theme";
import { useStytch, useStytchUser } from "../../../utils/useStytch";
import { getAuthCallbackUrl } from "../../../utils/getAuthCallbackUrl";

export type EmailVerificationStatus = "verified" | "pending" | "unverified";
export type PhoneVerificationStatus = "verified" | "unverified";

/**
 * Derives the email verification status from the Stytch user object.
 * - "verified" if the email exists in the Stytch user's emails array and is verified
 * - "pending" if the email exists but is not yet verified (magic link sent but not clicked)
 * - "unverified" if no email or email not found on the Stytch user
 */
export function useEmailVerificationStatus(
  email: string | null,
): EmailVerificationStatus {
  const { user } = useStytchUser();

  if (!email) return "unverified";
  if (!user) return "unverified";

  const stytchEmail = user.emails.find(
    (e) => e.email.toLowerCase() === email.toLowerCase(),
  );

  if (!stytchEmail) return "unverified";
  if (stytchEmail.verified) return "verified";
  return "pending"; // exists in Stytch but not yet verified
}

/**
 * Email verification badge with actionable buttons.
 * - Verified: green pill badge (read-only)
 * - Unverified: solid "Verify" button that sends a magic link
 * - Pending: outline "Resend" button + "Check your email" hint
 */
export function EmailVerificationBadge({
  status,
  email,
  onError,
}: {
  status: EmailVerificationStatus;
  email?: string;
  /** Surface delivery failures beside the field rather than silently swallowing them. */
  onError?: (message: string | null) => void;
}) {
  const t = useTranslations();
  const stytch = useStytch();
  const capturePress = usePressCapture("EmailVerificationBadge");
  const [isSending, setIsSending] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const justSentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (justSentTimer.current) clearTimeout(justSentTimer.current);
    };
  }, []);

  const sendMagicLink = useCallback(async () => {
    if (!email || isSending) return;
    setIsSending(true);
    try {
      const callbackUrl = getAuthCallbackUrl();
      await stytch.magicLinks.email.send(email, {
        login_magic_link_url: callbackUrl,
        signup_magic_link_url: callbackUrl,
      });
      onError?.(null);
      setJustSent(true);
      justSentTimer.current = setTimeout(() => setJustSent(false), 5000);
    } catch {
      onError?.(t("you.email_badge.failed_to_send"));
    } finally {
      setIsSending(false);
    }
  }, [email, isSending, onError, stytch.magicLinks.email, t]);

  // Verified — hide completely (no badge needed)
  if (status === "verified") {
    return null;
  }

  // Unverified — solid "Verify" button
  if (status === "unverified") {
    return (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("you.email_badge.verify")),
              "you.email_badge.verify",
            );
            void sendMagicLink();
          }}
          disabled={isSending}
          style={{
            paddingHorizontal: 10,
            paddingVertical: 3,
            borderRadius: 999,
            backgroundColor: EMBER.colors.warm,
            opacity: isSending ? 0.6 : 1,
          }}
        >
          {isSending ? (
            <ActivityIndicator size={10} color="#FFFFFF" />
          ) : (
            <Text
              className="font-dm-sans"
              style={{ fontSize: 11, fontWeight: "600", color: "#FFFFFF" }}
            >
              {t("you.email_badge.verify")}
            </Text>
          )}
        </Pressable>
        {justSent && (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              color: EMBER.colors.textFaint,
            }}
          >
            {t("you.email_badge.check_your_email")}
          </Text>
        )}
      </View>
    );
  }

  // Pending — outline "Resend" button + hint
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("you.email_badge.resend")),
            "you.email_badge.resend",
          );
          void sendMagicLink();
        }}
        disabled={isSending}
        style={{
          paddingHorizontal: 10,
          paddingVertical: 2,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: EMBER.colors.gold,
          backgroundColor: `${EMBER.colors.gold}10`,
          opacity: isSending ? 0.6 : 1,
        }}
      >
        {isSending ? (
          <ActivityIndicator size={10} color={EMBER.colors.gold} />
        ) : (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "500",
              color: EMBER.colors.gold,
            }}
          >
            {t("you.email_badge.resend")}
          </Text>
        )}
      </Pressable>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          color: justSent ? EMBER.colors.green : EMBER.colors.textFaint,
        }}
      >
        {justSent
          ? t("you.email_badge.sent")
          : t("you.email_badge.check_your_email")}
      </Text>
    </View>
  );
}

/**
 * Derives phone verification status from the Stytch user object.
 */
export function usePhoneVerificationStatus(
  phone: string | null,
): PhoneVerificationStatus {
  const { user } = useStytchUser();

  if (!phone || !user) return "unverified";

  const normalized = phone.replace(/[^\d+]/g, "");
  const stytchPhone = user.phone_numbers.find(
    (p) => p.phone_number.replace(/[^\d+]/g, "") === normalized,
  );

  if (!stytchPhone) return "unverified";
  if (stytchPhone.verified) return "verified";
  return "unverified";
}

/**
 * Phone verification flow.
 *
 * Returns the inline control (the "Verify" button, or the code-input +
 * "Submit" once an OTP has been sent) plus any error message. The control
 * is meant to sit in the field's trailing `right` slot, while the error is
 * surfaced *below* the field row by the caller — keeping it out of the
 * bordered input box where it would otherwise be clipped.
 *
 * - Verified: `control` is null (nothing to show).
 * - Unverified: "Verify" button that sends SMS OTP, then shows code input.
 */
export function usePhoneVerification({
  status,
  phone,
}: {
  status: PhoneVerificationStatus;
  phone?: string;
}): { control: React.ReactNode; error: string | null } {
  const t = useTranslations();
  const stytch = useStytch();
  const capturePress = usePressCapture("PhoneVerification");
  const [isSending, setIsSending] = useState(false);
  const [methodId, setMethodId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // OTP method IDs belong to one phone number. Drop an in-progress flow if
  // the caller switches numbers so a code can never authenticate a stale
  // destination.
  useEffect(() => {
    setMethodId(null);
    setCode("");
    setError(null);
  }, [phone]);

  const sendOtp = useCallback(async () => {
    if (!phone || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      const resp = await stytch.otps.sms.send(phone, {
        expiration_minutes: 10,
      });
      setMethodId(resp.method_id);
    } catch {
      setError(t("you.phone_badge.failed_to_send"));
    } finally {
      setIsSending(false);
    }
  }, [phone, isSending, stytch.otps.sms, t]);

  const verifyOtp = useCallback(async () => {
    if (!methodId || code.length !== 6) return;
    setIsVerifying(true);
    setError(null);
    try {
      await stytch.otps.authenticate(code, methodId, {
        session_duration_minutes: 60 * 24,
      });
      setMethodId(null);
      setCode("");
    } catch {
      setError(t("you.phone_badge.invalid_code"));
    } finally {
      setIsVerifying(false);
    }
  }, [methodId, code, stytch.otps, t]);

  // Verified — nothing to render
  if (status === "verified") {
    return { control: null, error: null };
  }

  // OTP sent — show code input + Submit
  if (methodId) {
    return {
      error,
      control: (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <TextInput
            value={code}
            onChangeText={(text) =>
              setCode(text.replace(/\D/g, "").slice(0, 6))
            }
            placeholder={t("you.phone_badge.code_placeholder")}
            placeholderTextColor={EMBER.colors.textFaint}
            keyboardType="number-pad"
            maxLength={6}
            style={{
              fontSize: 13,
              fontWeight: "500",
              color: EMBER.colors.text,
              borderWidth: 1,
              borderColor: EMBER.colors.border,
              borderRadius: 8,
              paddingHorizontal: 8,
              paddingVertical: 2,
              width: 70,
              textAlign: "center",
            }}
            onSubmitEditing={verifyOtp}
          />
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("you.phone_badge.submit")),
                "you.phone_badge.submit",
              );
              void verifyOtp();
            }}
            disabled={isVerifying || code.length !== 6}
            style={{
              paddingHorizontal: 10,
              paddingVertical: 3,
              borderRadius: 999,
              backgroundColor: EMBER.colors.warm,
              opacity: isVerifying || code.length !== 6 ? 0.5 : 1,
            }}
          >
            {isVerifying ? (
              <ActivityIndicator size={10} color="#FFFFFF" />
            ) : (
              <Text
                className="font-dm-sans"
                style={{ fontSize: 11, fontWeight: "600", color: "#FFFFFF" }}
              >
                {t("you.phone_badge.submit")}
              </Text>
            )}
          </Pressable>
        </View>
      ),
    };
  }

  // Unverified — show Verify button
  return {
    error,
    control: (
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("you.phone_badge.verify")),
            "you.phone_badge.verify",
          );
          void sendOtp();
        }}
        disabled={isSending}
        style={{
          paddingHorizontal: 10,
          paddingVertical: 3,
          borderRadius: 999,
          backgroundColor: EMBER.colors.warm,
          opacity: isSending ? 0.6 : 1,
        }}
      >
        {isSending ? (
          <ActivityIndicator size={10} color="#FFFFFF" />
        ) : (
          <Text
            className="font-dm-sans"
            style={{ fontSize: 11, fontWeight: "600", color: "#FFFFFF" }}
          >
            {t("you.phone_badge.verify")}
          </Text>
        )}
      </Pressable>
    ),
  };
}
